import { ValidationStringLengthEnum } from 'domain/enums/ValidationStringLengthEnum';
import { IMessageRepository } from 'domain/message/Message.repository';
import { CoupleButtonValidCancel } from 'primary/Components/CoupleButtonValidCancel/CoupleButtonValidCancel';
import { InputTextArea } from 'primary/Components/Input/InputTextArea';
import { InputWrapper } from 'primary/Components/Input/InputWrapper';
import { sendEventToastMessage } from 'primary/Components/Toast/Toast.helper';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useTranslate, UseTranslateReturn } from 'primary/hooks/useTranslate';
import { useYupValidationResolver } from 'primary/hooks/useYupValidationResolver';
import React from 'react';
import { useController, useForm } from 'react-hook-form';
import * as yup from 'yup';

interface SendMessageStructureProps {
  structureId: string;
  onSubmitted: () => void;
  onCancel: () => void;
}

export type SendMessageStructureFormData = {
  structureId: string;
  message: string;
};

const validationSchema = (t: UseTranslateReturn) =>
  yup.object({
    structureId: yup.string().required(t('general.forms.errors.required')),
    message: yup
      .string()
      .max(
        ValidationStringLengthEnum.LONG,
        t('general.forms.errors.tooLong', {
          maxChar: ValidationStringLengthEnum.LONG,
        }),
      )
      .required(),
  });

const onSubmitSendMessageStructure =
  (
    messageRepository: IMessageRepository,
    onSubmitted: () => void,
    t: UseTranslateReturn,
  ) =>
  (data: SendMessageStructureFormData) => {
    return messageRepository
      .sendMessageToStructure(data.message, parseInt(data.structureId))
      .then(() => {
        sendEventToastMessage(t('modals.sendMessageToStrucure.success'), 'success');
      })
      .finally(() => {
        onSubmitted();
      });
  };

export const SendMessageStructureForm = ({
  structureId,
  onSubmitted,
  onCancel,
}: SendMessageStructureProps) => {
  const { messageRepository } = useContextDependency();
  const resolver = useYupValidationResolver(validationSchema);
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
  } = useForm<SendMessageStructureFormData>({
    resolver,
    defaultValues: {
      structureId: structureId,
    },
  });
  const { field: messageField } = useController({ control: control, name: 'message' });

  if (!structureId) return null;

  const t = useTranslate();
  const onSubmit = onSubmitSendMessageStructure(
    messageRepository,
    () => onSubmitted(),
    t,
  );
  return (
    <form className={'-SendMessageStructure'} onSubmit={handleSubmit(onSubmit)}>
      <InputWrapper
        errors={errors}
        Input={InputTextArea}
        isSubmitting={isSubmitting}
        label={t('message.input.placeholder')}
        inputProps={{
          ...messageField,
          minRows: 3,
        }}
      />
      <CoupleButtonValidCancel isTypeSubmit={true} onClickCancel={onCancel} />
    </form>
  );
};
